import { Chart, registerables } from 'chart.js';
import React, { useEffect, useRef, useState } from 'react';
import { getDepartureTimeData, DepartureTimeRow } from '../../services/departureTimeService';

Chart.register(...registerables);

const departureOrder: string[] = [
  '12:00 a.m. to 4:59 a.m.',
  '5:00 a.m. to 5:29 a.m.',
  '5:30 a.m. to 5:59 a.m.',
  '6:00 a.m. to 6:29 a.m.',
  '6:30 a.m. to 6:59 a.m.',
  '7:00 a.m. to 7:29 a.m.',
  '7:30 a.m. to 7:59 a.m.',
  '8:00 a.m. to 8:29 a.m.',
  '8:30 a.m. to 8:59 a.m.',
  '9:00 a.m. to 9:59 a.m.',
  '10:00 a.m. to 10:59 a.m.',
  '11:00 a.m. to 11:59 a.m.',
  '12:00 p.m. to 3:59 p.m.',
  '4:00 p.m. to 11:59 p.m.'
];

const normalizeBin = (label: string | null | undefined) => String(label || '').replace(/^Total:\s*/i, '').trim();
const canonicalKey = (label: string | null | undefined) =>
  normalizeBin(label)
    .toLowerCase()
    .replace(/\s+/g, '')
    .replace(/\./g, '');
const canonicalOrder = departureOrder.map(canonicalKey);
const canonicalToLabel = new Map(canonicalOrder.map((k, i) => [k, departureOrder[i]]));
const parseStartMinutes = (label: string): number => {
  const normalized = normalizeBin(label).toLowerCase();
  const m = normalized.match(/(\d{1,2}):(\d{2})\s*([ap])\.?m\.?/);
  if (!m) return Number.MAX_SAFE_INTEGER;
  let hour = Number(m[1]);
  const minute = Number(m[2]);
  const ap = m[3];
  if (ap === 'a') {
    if (hour === 12) hour = 0;
  } else if (hour !== 12) {
    hour += 12;
  }
  return hour * 60 + minute;
};

interface DepartureTimeChartProps {
  location: string | null;
  onTopWindowChange?: (windowLabel: string | null) => void;
}

export const DepartureTimeChart: React.FC<DepartureTimeChartProps> = ({ location, onTopWindowChange }) => {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const chartRef = useRef<Chart | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<DepartureTimeRow[]>([]);

  useEffect(() => {
    const pickTopWindow = (data: DepartureTimeRow[]): string | null => {
      if (!data.length) return null;
      const years = Array.from(new Set(data.map((r) => Number(r.year)))).sort((a, b) => a - b);
      const latestYear = years[years.length - 1];
      const latestRows = data.filter((r) => Number(r.year) === latestYear);
      const topRow = [...latestRows].sort((a, b) => Number(b.percent ?? b.estimate) - Number(a.percent ?? a.estimate))[0];
      return normalizeBin(topRow?.departure_time_bin) || null;
    };

    let cancelled = false;
    const load = async () => {
      if (!location) {
        setRows([]);
        onTopWindowChange?.(null);
        setLoading(false);
        return;
      }
      setLoading(true);
      setError(null);
      try {
        const data = await getDepartureTimeData(location);
        if (!cancelled) {
          setRows(data);
          onTopWindowChange?.(pickTopWindow(data));
        }
      } catch (e) {
        if (!cancelled) onTopWindowChange?.(null);
        if (!cancelled) setError(e instanceof Error ? e.message : 'Failed to load departure time data');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [location]);

  useEffect(() => {
    if (!canvasRef.current) return;
    if (!rows.length) {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
      return;
    }

    const ctx = canvasRef.current.getContext('2d');
    if (!ctx) return;

    // Use latest year with data
    const years = Array.from(new Set(rows.map((r) => r.year))).sort((a, b) => a - b);
    const latestYear = years[years.length - 1];
    const latestRows = rows.filter((r) => r.year === latestYear);
    const total = latestRows.reduce((sum, r) => sum + (Number(r.estimate) || 0), 0);

    // Deterministic axis order: always render bins from earliest to latest.
    const byBin = new Map<string, number>();
    latestRows.forEach((r) => {
      const bin = canonicalKey(r.departure_time_bin);
      const value = r.percent != null
        ? Number(r.percent)
        : (total ? ((Number(r.estimate) || 0) / total) * 100 : 0);
      byBin.set(bin, (byBin.get(bin) || 0) + value);
    });

    const orderedCanonical: string[] = [];
    canonicalOrder.forEach((k) => {
      if (byBin.has(k)) orderedCanonical.push(k);
    });
    Array.from(byBin.keys())
      .filter((k) => !canonicalOrder.includes(k))
      .sort((a, b) => parseStartMinutes(a) - parseStartMinutes(b))
      .forEach((k) => orderedCanonical.push(k));

    const labels = orderedCanonical.map((k) => canonicalToLabel.get(k) || normalizeBin(k));
    const values = orderedCanonical.map((k) => byBin.get(k) || 0);

    if (chartRef.current) chartRef.current.destroy();

    chartRef.current = new Chart(ctx, {
      type: 'bar',
      data: {
        labels,
        datasets: [
          {
            label: `Share of workers by departure time (${latestYear}) - chronological`,
            data: values,
            backgroundColor: 'rgba(37, 99, 235, 0.8)',
            borderRadius: 6,
            maxBarThickness: 40
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { position: 'top' },
          tooltip: {
            callbacks: {
              label: (ctx) => `${ctx.parsed.y.toFixed(1)}%`
            }
          }
        },
        scales: {
          y: {
            beginAtZero: true,
            title: { display: true, text: 'Percent of workers' }
          },
          x: {
            title: { display: true, text: 'Departure time to work (earliest to latest)' },
            ticks: { maxRotation: 45, minRotation: 0 }
          }
        }
      }
    });

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [rows]);

  if (loading) {
    return <div style={{ padding: 24, textAlign: 'center' }}>Loading departure time data...</div>;
  }
  if (error) {
    return <div style={{ padding: 24, color: '#dc2626' }}>{error}</div>;
  }

  if (!rows.length) {
    return <p style={{ padding: 24 }}>No departure time data for {location}.</p>;
  }

  return (
    <div style={{ height: 320 }}>
      <canvas ref={canvasRef} />
    </div>
  );
};

